// Story 11.4 — Filtros del LogPanel: checkboxes nivel + search.

import { Search } from 'lucide-react';
import { useId } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ALL_LEVELS, type LogFilters } from '@/lib/log-buffer';
import { cn } from '@/lib/utils';

interface Props {
  filters: LogFilters;
  onChange: (next: LogFilters) => void;
}

const LEVEL_LABEL: Record<(typeof ALL_LEVELS)[number], string> = {
  debug: 'Debug',
  info: 'Info',
  warn: 'Warn',
  error: 'Error',
};

const LEVEL_DOT_CLASS: Record<(typeof ALL_LEVELS)[number], string> = {
  debug: 'bg-muted-foreground',
  info: 'bg-info',
  warn: 'bg-warning',
  error: 'bg-destructive',
};

export function LogFiltersBar({ filters, onChange }: Props): React.ReactElement {
  const searchId = useId();

  function toggleLevel(level: (typeof ALL_LEVELS)[number]): void {
    const set = new Set(filters.levels);
    if (set.has(level)) set.delete(level);
    else set.add(level);
    onChange({
      ...filters,
      levels: ALL_LEVELS.filter((l) => set.has(l)),
    });
  }

  return (
    <div className="flex flex-wrap items-center gap-2 px-3 py-2 text-xs">
      <div className="relative w-44">
        <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-muted-foreground" />
        <Input
          id={searchId}
          type="search"
          value={filters.search}
          onChange={(e) => onChange({ ...filters, search: e.target.value })}
          placeholder="Buscar en logs"
          className="h-7 pl-7 text-xs"
          aria-label="Buscar en logs"
        />
      </div>
      <fieldset className="flex items-center gap-1.5 border-0 p-0">
        <legend className="sr-only">Filtros de nivel</legend>
        {ALL_LEVELS.map((level) => {
          const active = filters.levels.includes(level);
          return (
            <Label
              key={level}
              className={cn(
                'inline-flex cursor-pointer select-none items-center gap-1 rounded border px-2 py-0.5 text-[10px] font-semibold uppercase',
                active
                  ? 'border-border bg-muted text-foreground'
                  : 'border-transparent text-muted-foreground hover:bg-muted/30',
              )}
            >
              <input
                type="checkbox"
                className="sr-only"
                checked={active}
                onChange={() => toggleLevel(level)}
              />
              <span
                aria-hidden
                className={cn('h-1.5 w-1.5 rounded-full', LEVEL_DOT_CLASS[level])}
              />
              {LEVEL_LABEL[level]}
            </Label>
          );
        })}
      </fieldset>
    </div>
  );
}
